import React, { useState, useRef, useEffect } from 'react'
import Timekeeper from 'react-timekeeper'

const to12 = (value24 = '') => {
  if (!value24 || value24.indexOf(':') === -1) return ''
  const [hhStr, mmStr] = value24.split(':')
  let h = parseInt(hhStr || '0', 10)
  const m = parseInt(mmStr || '0', 10)
  const ap = h >= 12 ? 'PM' : 'AM'
  h = h % 12
  if (h === 0) h = 12
  const mm = String(m).padStart(2, '0')
  return `${h}:${mm} ${ap}`
}

const to24 = (value12 = '') => {
  if (!value12) return ''
  const parts = value12
    .toUpperCase()
    .replace(/\s+/g, ' ')
    .trim()
    .split(' ')
  const [hStr, mmPart] = (parts[0] || '').split(':')
  const ap = parts[1] || 'AM'
  let h = parseInt(hStr || '12', 10)
  const m = parseInt(mmPart || '0', 10)
  const isPM = ap === 'PM'
  if (isPM && h < 12) h += 12
  if (!isPM && h === 12) h = 0
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
}

const cmp24 = (a = '', b = '') => {
  if (!a || !b) return 1
  const [ah, am] = a.split(':').map((x) => parseInt(x || '0', 10))
  const [bh, bm] = b.split(':').map((x) => parseInt(x || '0', 10))
  if (ah !== bh) return ah - bh
  return am - bm
}

export default function ClockTimePicker({
  value24,
  min24,
  onChange,
  placeholder
}) {
  const [open, setOpen] = useState(false)
  const [display, setDisplay] = useState(to12(value24))
  const [pending24, setPending24] = useState(value24 || '')
  const [pendingDisp, setPendingDisp] = useState(to12(value24))
  const ref = useRef(null)

  useEffect(() => {
    setDisplay(to12(value24))
    setPending24(value24 || '')
    setPendingDisp(to12(value24))
  }, [value24])

  useEffect(() => {
    const handler = (e) => {
      if (open && ref.current && !ref.current.contains(e.target)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  const parseToObj = (v24) => {
    if (!v24) return { hour: 12, minute: 0, meridiem: 'am' }
    const [hhStr, mmStr] = v24.split(':')
    let hh = parseInt(hhStr || '0', 10)
    const mm = parseInt(mmStr || '0', 10)
    const mer = hh >= 12 ? 'pm' : 'am'
    hh = hh % 12
    if (hh === 0) hh = 12
    return { hour: hh, minute: mm, meridiem: mer }
  }

  const handleChange = (t) => {
    // t can be object or string depending on lib version
    const timeStr =
      typeof t === 'string'
        ? t
        : `${t.hour}:${String(t.minute).padStart(2, '0')} ${t.meridiem}`
    // round to 5-minute steps
    const parts = timeStr.split(' ')
    const [hStr, mStr] = (parts[0] || '').split(':')
    const ap = (parts[1] || 'AM').toUpperCase()
    const roundedMin = String(
      Math.round(parseInt(mStr || '0', 10) / 5) * 5
    ).padStart(2, '0')
    let hour12 = parseInt(hStr || '12', 10)
    hour12 = hour12 % 12
    if (hour12 === 0) hour12 = 12
    const normalized = `${hour12}:${roundedMin} ${ap}`
    const candidate24 = to24(normalized)
    setPending24(candidate24)
    setPendingDisp(normalized)
  }

  return (
    <div className='position-relative' ref={ref}>
      <input
        type='text'
        className='form-control'
        readOnly
        placeholder={placeholder}
        value={display || ''}
        onClick={() => {
          setPending24(value24 || '')
          setPendingDisp(to12(value24))
          setOpen(true)
        }}
      />
      {open && (
        <div
          style={{ position: 'absolute', zIndex: 9999 }}
          className='bg-white border rounded shadow p-2'
        >
          <Timekeeper
            time={parseToObj(pending24)}
            onChange={handleChange}
            switchToMinuteOnHourSelect
            coarseMinutes={5}
          />
          <div className='text-end mt-2'>
            <button
              type='button'
              className='btn btn-sm btn-primary'
              onClick={() => {
                let out24 = pending24
                if (min24 && cmp24(out24, min24) < 0) out24 = min24
                setDisplay(to12(out24))
                onChange(out24)
                setOpen(false)
              }}
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
